import { useMemo, useRef, useState } from "react";
import {
  Layers,
  ImagePlus,
  Type,
  Download,
  Loader2,
  AlertTriangle,
  Trash2,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { useI18n } from "../lib/i18n.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";

function uid() {
  return globalThis.crypto?.randomUUID?.() || `layer-${Date.now()}-${Math.random()}`;
}

function makeTextLayer(text = "Your text here") {
  return {
    id: uid(),
    kind: "text",
    visible: true,
    x: 50,
    y: 50,
    opacity: 1,
    rotation: 0,
    text,
    color: "#ffffff",
    fontSize: 56,
    fontWeight: 700,
    letterSpacing: 0,
    align: "center",
  };
}

function makeImageLayer(src) {
  return {
    id: uid(),
    kind: "image",
    visible: true,
    x: 50,
    y: 50,
    opacity: 1,
    rotation: 0,
    width: 55,
    height: 55,
    src,
  };
}

const CANVAS_PRESETS = [
  { id: "square", label: "Square", ratio: "1 / 1" },
  { id: "portrait", label: "Portrait", ratio: "4 / 5" },
  { id: "landscape", label: "Landscape", ratio: "16 / 9" },
];

const EXPORT_SIZES = {
  square: { width: 1080, height: 1080 },
  portrait: { width: 1080, height: 1350 },
  landscape: { width: 1600, height: 900 },
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Could not generate PNG file."));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function ImageStudio() {
  const { t } = useI18n();
  const uploadRef = useRef(null);
  const [canvasPreset, setCanvasPreset] = useState(CANVAS_PRESETS[0].id);
  const [layers, setLayers] = useState(() => [makeTextLayer("Welcome to Image Studio")]);
  const [selectedId, setSelectedId] = useState(() => layers[0]?.id ?? null);
  const [taskBanner, setTaskBanner] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const selectedLayer = useMemo(
    () => layers.find((layer) => layer.id === selectedId) || null,
    [layers, selectedId]
  );

  const updateLayer = (id, patch) => {
    setLayers((prev) =>
      prev.map((layer) => (layer.id === id ? { ...layer, ...patch } : layer))
    );
  };

  const deleteLayer = (id) => {
    setLayers((prev) => {
      const next = prev.filter((layer) => layer.id !== id);
      if (!next.length) {
        const fallback = makeTextLayer(t("imageStudio.newCaption", "New caption"));
        setSelectedId(fallback.id);
        return [fallback];
      }
      if (selectedId === id) setSelectedId(next[next.length - 1].id);
      return next;
    });
  };

  const moveLayer = (id, direction) => {
    setLayers((prev) => {
      const idx = prev.findIndex((layer) => layer.id === id);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy;
    });
  };

  const addTextLayer = (text = t("imageStudio.newTextOverlay", "New text overlay")) => {
    const layer = makeTextLayer(text);
    setLayers((prev) => [...prev, layer]);
    setSelectedId(layer.id);
  };

  const addImageLayer = (src) => {
    const layer = makeImageLayer(src);
    setLayers((prev) => [...prev, layer]);
    setSelectedId(layer.id);
  };

  const onPickImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => addImageLayer(reader.result);
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  usePendingTask("/image-studio", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    if (task.prompt?.trim()) addTextLayer(task.prompt.trim());
  });
  const done = useHomeTaskDone(false, null, Boolean(taskBanner));

  const activePreset = CANVAS_PRESETS.find((preset) => preset.id === canvasPreset) || CANVAS_PRESETS[0];

  const exportPng = async () => {
    if (!layers.length || exporting) return;
    setExportError(null);
    setExporting(true);
    try {
      const size = EXPORT_SIZES[canvasPreset] || EXPORT_SIZES.square;
      const canvas = document.createElement("canvas");
      canvas.width = size.width;
      canvas.height = size.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error(
          t(
            "imageStudio.canvasUnavailable",
            "Canvas rendering is not available in this browser."
          )
        );
      }

      ctx.fillStyle = "#0a0c14";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (const layer of layers) {
        if (!layer.visible) continue;

        const x = (layer.x / 100) * canvas.width;
        const y = (layer.y / 100) * canvas.height;
        const opacity = Number.isFinite(layer.opacity) ? layer.opacity : 1;
        const rotation = (Number(layer.rotation) || 0) * (Math.PI / 180);

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rotation);
        ctx.globalAlpha = Math.max(0, Math.min(1, opacity));

        if (layer.kind === "image" && layer.src) {
          try {
            const image = await loadImage(layer.src);
            const width = ((Number(layer.width) || 1) / 100) * canvas.width;
            const height = ((Number(layer.height) || 1) / 100) * canvas.height;
            ctx.drawImage(image, -width / 2, -height / 2, width, height);
          } catch {
            // Skip unreadable layer images.
          }
        } else if (layer.kind === "text") {
          const fontSize = Math.max(8, Number(layer.fontSize) || 24);
          const fontWeight = Number(layer.fontWeight) || 700;
          const letterSpacing = Number(layer.letterSpacing) || 0;
          const text = String(layer.text || "").trim();
          const lines = text ? text.split("\n") : [];
          const lineHeight = fontSize * 1.15;
          const maxBoxWidth = canvas.width * 0.9;
          const align = layer.align || "center";
          const startY = -((Math.max(lines.length, 1) - 1) * lineHeight) / 2;

          ctx.fillStyle = layer.color || "#ffffff";
          ctx.font = `${fontWeight} ${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
          ctx.textBaseline = "middle";
          ctx.shadowColor = "rgba(0,0,0,0.55)";
          ctx.shadowBlur = 8;
          ctx.textAlign = align;

          let drawX = 0;
          if (align === "left") drawX = -maxBoxWidth / 2;
          if (align === "right") drawX = maxBoxWidth / 2;

          for (let i = 0; i < lines.length; i += 1) {
            const line = lines[i];
            const drawY = startY + i * lineHeight;
            if (!line) continue;
            if (letterSpacing === 0) {
              ctx.fillText(line, drawX, drawY, maxBoxWidth);
              continue;
            }
            const chars = [...line];
            let cursorX = drawX;
            if (align === "center") {
              const totalWidth =
                chars.reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) +
                letterSpacing * Math.max(chars.length - 1, 0);
              cursorX = -totalWidth / 2;
            } else if (align === "right") {
              const totalWidth =
                chars.reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) +
                letterSpacing * Math.max(chars.length - 1, 0);
              cursorX = maxBoxWidth / 2 - totalWidth;
            }
            for (const ch of chars) {
              ctx.fillText(ch, cursorX, drawY);
              cursorX += ctx.measureText(ch).width + letterSpacing;
            }
          }
        }

        ctx.restore();
      }

      const blob = await canvasToBlob(canvas);
      downloadBlob(blob, `image-studio-${Date.now()}.png`);
    } catch (error) {
      setExportError(
        error.message || t("imageStudio.exportFailed", "Failed to export PNG.")
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader
        icon={Layers}
        title={t("home.feature.imageStudio.title", "Image Studio")}
        subtitle={t(
          "home.feature.imageStudio.desc",
          "Layer images and add styled text overlays for social posts and ads."
        )}
        right={
          <button onClick={exportPng} disabled={exporting || !layers.length} className="btn-primary">
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("common.exporting", "Exporting...")}
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                {t("imageStudio.exportPng", "Export PNG")}
              </>
            )}
          </button>
        }
      />

      <TaskBanner
        label={t("home.feature.imageStudio.title", "Image Studio")}
        prompt={taskBanner}
        completed={done}
      />
      {exportError && (
        <div className="mx-4 md:mx-10 mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{exportError}</span>
        </div>
      )}

      <div className="flex-1 px-4 md:px-10 py-6 grid lg:grid-cols-[1fr,390px] gap-6 max-w-7xl mx-auto w-full min-h-0">
        <section className="space-y-4 min-w-0">
          <div className="card flex flex-wrap items-center gap-2">
            <button onClick={() => uploadRef.current?.click()} className="btn-secondary">
              <ImagePlus className="h-4 w-4" />
              {t("imageStudio.addImageLayer", "Add image layer")}
            </button>
            <input
              ref={uploadRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickImage}
            />
            <button onClick={() => addTextLayer()} className="btn-secondary">
              <Type className="h-4 w-4" />
              {t("imageStudio.addTextOverlay", "Add text overlay")}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-white/50">
                {t("imageStudio.canvas", "Canvas")}
              </span>
              <select
                value={canvasPreset}
                onChange={(e) => setCanvasPreset(e.target.value)}
                className="input !w-auto !py-1.5 text-xs"
              >
                {CANVAS_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {t(
                      `imageStudio.preset.${preset.id}`,
                      preset.label
                    )}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-4">
            <div
              className="relative mx-auto w-full max-w-3xl rounded-xl overflow-hidden border border-white/10 bg-ink-900"
              style={{ aspectRatio: activePreset.ratio }}
            >
              {layers.map((layer) => {
                if (!layer.visible) return null;
                const style = {
                  position: "absolute",
                  left: `${layer.x}%`,
                  top: `${layer.y}%`,
                  transform: `translate(-50%, -50%) rotate(${layer.rotation}deg)`,
                  opacity: layer.opacity,
                  zIndex: layers.findIndex((entry) => entry.id === layer.id) + 1,
                  cursor: "pointer",
                };
                if (layer.kind === "image") {
                  return (
                    <img
                      key={layer.id}
                      src={layer.src}
                      alt=""
                      onClick={() => setSelectedId(layer.id)}
                      style={{ ...style, width: `${layer.width}%`, height: `${layer.height}%`, objectFit: "cover" }}
                      className={[
                        "rounded-md border",
                        selectedId === layer.id ? "border-consensus-400" : "border-transparent",
                      ].join(" ")}
                    />
                  );
                }
                return (
                  <div
                    key={layer.id}
                    onClick={() => setSelectedId(layer.id)}
                    style={{
                      ...style,
                      color: layer.color,
                      fontSize: `${layer.fontSize}px`,
                      fontWeight: layer.fontWeight,
                      letterSpacing: `${layer.letterSpacing}px`,
                      textAlign: layer.align,
                      maxWidth: "90%",
                      lineHeight: 1.1,
                      textShadow: "0 1px 10px rgba(0,0,0,0.5)",
                    }}
                    className={selectedId === layer.id ? "outline outline-1 outline-consensus-400 rounded px-1" : ""}
                  >
                    {layer.text}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="card space-y-4 min-h-0 overflow-y-auto">
          <div>
            <h2 className="text-sm font-semibold mb-2">
              {t("imageStudio.layers", "Layers")}
            </h2>
            <div className="space-y-2">
              {[...layers].reverse().map((layer) => {
                const originalIdx = layers.findIndex((entry) => entry.id === layer.id);
                const canMoveUp = originalIdx < layers.length - 1;
                const canMoveDown = originalIdx > 0;
                return (
                  <div
                    key={layer.id}
                    className={[
                      "rounded-lg border px-2 py-2 flex items-center gap-2",
                      selectedId === layer.id
                        ? "border-consensus-500/40 bg-consensus-500/10"
                        : "border-white/10 bg-white/[0.02]",
                    ].join(" ")}
                  >
                    <button onClick={() => setSelectedId(layer.id)} className="text-left flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {layer.kind === "image"
                          ? t("imageStudio.imageLayer", "Image layer")
                          : layer.text || t("imageStudio.textOverlay", "Text overlay")}
                      </div>
                      <div className="text-[10px] text-white/45 uppercase tracking-wide">
                        {layer.kind}
                      </div>
                    </button>
                    <button
                      onClick={() => updateLayer(layer.id, { visible: !layer.visible })}
                      className="btn-ghost !p-1.5"
                      title={
                        layer.visible
                          ? t("imageStudio.hideLayer", "Hide layer")
                          : t("imageStudio.showLayer", "Show layer")
                      }
                    >
                      {layer.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      onClick={() => moveLayer(layer.id, +1)}
                      className="btn-ghost !p-1.5"
                      disabled={!canMoveUp}
                      title={t("common.moveUp", "Move up")}
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => moveLayer(layer.id, -1)}
                      className="btn-ghost !p-1.5"
                      disabled={!canMoveDown}
                      title={t("common.moveDown", "Move down")}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => deleteLayer(layer.id)}
                      className="btn-ghost !p-1.5 text-red-300 hover:text-red-200"
                      title={t("common.delete", "Delete layer")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedLayer && (
            <div className="space-y-3 border-t border-white/10 pt-3">
              <h3 className="text-sm font-semibold">
                {t("imageStudio.selectedLayer", "Selected Layer")}
              </h3>

              <div className="grid grid-cols-2 gap-2">
                <NumericField
                  label={t("imageStudio.x", "X (%)")}
                  value={selectedLayer.x}
                  onChange={(value) => updateLayer(selectedLayer.id, { x: Number(value) || 0 })}
                />
                <NumericField
                  label={t("imageStudio.y", "Y (%)")}
                  value={selectedLayer.y}
                  onChange={(value) => updateLayer(selectedLayer.id, { y: Number(value) || 0 })}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <NumericField
                  label={t("imageStudio.opacity", "Opacity")}
                  value={selectedLayer.opacity}
                  step="0.05"
                  min="0"
                  max="1"
                  onChange={(value) => updateLayer(selectedLayer.id, { opacity: Number(value) || 0 })}
                />
                <NumericField
                  label={t("imageStudio.rotation", "Rotation")}
                  value={selectedLayer.rotation}
                  step="1"
                  onChange={(value) => updateLayer(selectedLayer.id, { rotation: Number(value) || 0 })}
                />
              </div>

              {selectedLayer.kind === "image" ? (
                <div className="grid grid-cols-2 gap-2">
                  <NumericField
                    label={t("imageStudio.width", "Width (%)")}
                    value={selectedLayer.width}
                    onChange={(value) => updateLayer(selectedLayer.id, { width: Number(value) || 1 })}
                  />
                  <NumericField
                    label={t("imageStudio.height", "Height (%)")}
                    value={selectedLayer.height}
                    onChange={(value) => updateLayer(selectedLayer.id, { height: Number(value) || 1 })}
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="label">{t("common.text", "Text")}</label>
                    <textarea
                      value={selectedLayer.text}
                      onChange={(e) => updateLayer(selectedLayer.id, { text: e.target.value })}
                      rows={3}
                      className="textarea"
                    />
                    <SpeechToTextButton
                      className="mt-2"
                      onTranscript={(text) =>
                        updateLayer(selectedLayer.id, {
                          text: appendTranscript(selectedLayer.text, text),
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <NumericField
                      label={t("imageStudio.fontSize", "Font size")}
                      value={selectedLayer.fontSize}
                      onChange={(value) => updateLayer(selectedLayer.id, { fontSize: Number(value) || 12 })}
                    />
                    <NumericField
                      label={t("imageStudio.weight", "Weight")}
                      value={selectedLayer.fontWeight}
                      step="100"
                      onChange={(value) => updateLayer(selectedLayer.id, { fontWeight: Number(value) || 400 })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <NumericField
                      label={t("imageStudio.letterSpacing", "Letter spacing")}
                      value={selectedLayer.letterSpacing}
                      onChange={(value) => updateLayer(selectedLayer.id, { letterSpacing: Number(value) || 0 })}
                    />
                    <div>
                      <label className="label">{t("imageStudio.color", "Color")}</label>
                      <input
                        type="color"
                        value={selectedLayer.color}
                        onChange={(e) => updateLayer(selectedLayer.id, { color: e.target.value })}
                        className="input h-10 p-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label">{t("imageStudio.alignment", "Alignment")}</label>
                    <select
                      value={selectedLayer.align}
                      onChange={(e) => updateLayer(selectedLayer.id, { align: e.target.value })}
                      className="input"
                    >
                      <option value="left">{t("imageStudio.align.left", "Left")}</option>
                      <option value="center">{t("imageStudio.align.center", "Center")}</option>
                      <option value="right">{t("imageStudio.align.right", "Right")}</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function NumericField({ label, value, onChange, step = "1", min, max }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        min={min}
        max={max}
        className="input"
      />
    </div>
  );
}
